// @flow
import type {HOC} from 'recompose'
import type {State} from '../state'

import {initSentry} from '../utils/sentry'
import React, {Fragment} from 'react'
import ReactGA from 'react-ga'
import {connect} from 'react-redux'
import {Redirect, Route, Switch, withRouter} from 'react-router-dom'
import {compose, lifecycle} from 'recompose'
import styled, {
  StyleSheetManager,
  ThemeProvider,
  createGlobalStyle
} from 'styled-components'

import requireLoginStatusContainer from '../containers/requireLoginStatusContainer'
import userContainer from '../containers/userContainer'
import {formatMessage} from '../i18n'
import {accountListSelector, allCardsListSelector} from '../selectors'
import {
  withConfig,
  withStorageData,
  withUserAndCardData,
  withUserDataOnly
} from '../withData'

// auth
import Login from './auth/login/Login'
import Registration from './auth/registration/Registration'
import RequestCard from './auth/requestCard/RequestCard'
// header, footer
import Footer from './Footer'
import Header from './header/Header'
// profile
import Profile from './profile/Profile'
// accounts
import Account from './accounts/Account'
import AccountManagement from './accounts/AccountManagement'
import Accounts from './accounts/Accounts'
import BankStatements from './accounts/bankStatements/BankStatements'
import AccountCardsManagement from './accounts/cards/AccountCardsManagement'
import GuestsManagement from './accounts/guests/GuestsManagement'
import Transactions from './accounts/transactions/Transactions'
// cards
import CardManagement from './cards/CardManagement'
import CardsManagement from './cards/CardsManagement'
// other
import isPropValid from '@emotion/is-prop-valid'
import GeneralError from './GeneralError'
import {LoadingOverlay, LoadingPlaceholder} from './ui'
import {theme} from './ui/theme'

/*eslint no-unused-expressions: ["error", { "allowTaggedTemplates": true }]*/
const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'basier_circle';
    src: url('/fonts/basier_circle_regular-webfont.woff2') format('woff2'),
         url('/fonts/basier_circle_regular-webfont.woff') format('woff'),
         url('/fonts/basier_circle_regular-webfont.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;

}

  @font-face {
    font-family: 'basier_circle';
    src: url('/fonts/basier_circle_medium-webfont.woff2') format('woff2'),
         url('/fonts/basier_circle_medium-webfont.woff') format('woff'),
         url('/fonts/basier_circle_medium-webfont.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;

}

  *, *::after, *::before {
    box-sizing: border-box;
  }
  html {
    height: 100%;
  }

  body {
    background-color: #fff;
    background-repeat: no-repeat;
    height: 100%;
  }

  #root,
  #root > div {
    height: 100%;
  }
`

const StyledApp = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`

const Content = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
`

const Auth = () => (
  <StyledApp isAuth>
    <Content>
      <Switch>
        <Route exact path="/auth/registration" component={Registration} />
        <Route exact path="/auth/login" component={Login} />
        <Route exact path="/auth/request-card" component={RequestCard} />
        <Redirect to="/auth/login" />
      </Switch>
    </Content>
  </StyledApp>
)

const _LoggedApp = ({singleAccount, firstAccountId, singleCard}) => (
  <StyledApp>
    <Header
      singleAccount={singleAccount}
      singleCard={singleCard}
      firstAccountId={firstAccountId}
    />
    <Content>
      <Switch>
        <Route exact path="/404" render={() => 'Not found'} />
        {singleAccount ? (
          <Redirect
            exact
            from="/accounts"
            to={`/accounts/${firstAccountId}/transactions`}
          />
        ) : (
          <Route exact path="/accounts" component={Accounts} />
        )}
        <Redirect
          exact
          from="/accounts/:accountId"
          to="/accounts/:accountId/transactions"
        />
        <Route
          path="/accounts/:accountId"
          render={props => (
            <Account {...props}>
              <Switch>
                <Route
                  exact
                  path="/accounts/:accountId/transactions"
                  component={Transactions}
                />
                <Route
                  exact
                  path="/accounts/:accountId/management"
                  component={AccountManagement}
                />
                <Route
                  exact
                  path="/accounts/:accountId/cards"
                  component={AccountCardsManagement}
                />
                <Route
                  exact
                  path="/accounts/:accountId/cards/:cardId"
                  component={CardManagement}
                />
                <Route
                  exact
                  path="/accounts/:accountId/bank-statements"
                  component={BankStatements}
                />
                <Route
                  exact
                  path="/accounts/:accountId/guests"
                  component={GuestsManagement}
                />
              </Switch>
            </Account>
          )}
        />
        <Route
          path="/cards"
          render={props => (
            <CardsManagement {...props}>
              <Route exact path="/cards/:cardId" component={CardManagement} />
            </CardsManagement>
          )}
        />
        <Route path="/profile" component={Profile} />
        <Redirect to="/accounts" />
      </Switch>
    </Content>
    <Footer />
  </StyledApp>
)

const LoadingUserLoggedInApp = () => (
  <StyledApp>
    <Header loadingUser />
    <Content>
      <LoadingPlaceholder title={formatMessage('loadingData')} />
    </Content>
    <Footer />
  </StyledApp>
)

const enhanceLoggedApp: HOC<*, *> = compose(
  // withRouter is needed to force rerender when route changes
  withRouter,
  withUserDataOnly({loadingComponent: () => <LoadingUserLoggedInApp />}),
  // start loading card settings immediately, but in background
  withUserAndCardData(),
  connect((state: State, props) => {
    const accounts = accountListSelector(state)
    const cards = allCardsListSelector(state)

    const singleAccount = accounts.length === 1
    const firstAccountId = singleAccount ? accounts[0].id : ''
    const singleCard = cards.length === 1

    return {singleAccount, firstAccountId, singleCard}
  })
)

const LoggedApp = enhanceLoggedApp(_LoggedApp)

const App = ({loginStatus}) => (
  <StyleSheetManager shouldForwardProp={isPropValid}>
    <ThemeProvider theme={theme}>
      <Fragment>
        <GlobalStyle />
        <GeneralError />
        {loginStatus ? <LoggedApp /> : <Auth />}
      </Fragment>
    </ThemeProvider>
  </StyleSheetManager>
)

const enhanceApp: HOC<*, *> = compose(
  // withRouter is needed to force rerender when route changes
  withRouter,
  withConfig({
    loadingComponent: () => <LoadingOverlay isOpen />,
    onConfigPulled: () => {
      window.appVersion = window.config.webAppVersion
      initSentry({dsn: window.config.sentry, app: 'web'})
      ReactGA.initialize(window.config.gaTrackingId, {debug: false})
    }
  }),
  withStorageData({loadingComponent: () => <LoadingOverlay isOpen />}),
  requireLoginStatusContainer,
  userContainer,
  lifecycle({
    componentDidMount() {
      // FIXME just a quick hack
      if (window.config.titleSuffix) {
        document.title = `${document.title} ${window.config.titleSuffix}`
      }
      // GA
      // we don't have user yet, so we don't know about userId
      ReactGA.pageview(this.props.location.pathname)
    },
    componentDidUpdate(prevProps) {
      // GA
      if (prevProps.location.pathname !== this.props.location.pathname) {
        ReactGA.set({userId: this.props.user && this.props.user.id})
        ReactGA.pageview(this.props.location.pathname)
      }
    }
  })
)

export default enhanceApp(App)
